import { useState } from "react";
import { Check, Monitor, Palette, X } from "lucide-react";

import { cn } from "@/shared/lib/cn";

import { useThemeStore } from "../model/themeStore";
import { THEME_PRESETS, type ThemePreset } from "../model/themes";

/**
 * Floating theme picker. Shows a small "Theme" pill in the bottom-left;
 * clicking opens a panel with all presets and a live swatch preview.
 * Dev-only — production is locked to the forest (brand) theme.
 */

function SwatchPreview({ preset }: { preset: ThemePreset }) {
  return (
    <div className="flex h-12 w-full overflow-hidden rounded-md ring-1 ring-border-strong/40">
      <div className="flex-1" style={{ backgroundColor: preset.swatch.background }} />
      <div className="flex-1" style={{ backgroundColor: preset.swatch.surface }} />
      <div className="flex-1" style={{ backgroundColor: preset.swatch.primary }} />
      <div className="flex-1" style={{ backgroundColor: preset.swatch.accent }} />
    </div>
  );
}

export function ThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const themeId = useThemeStore((s) => s.themeId);
  const setTheme = useThemeStore((s) => s.setTheme);

  if (!import.meta.env.DEV) return null;

  return (
    <div className="fixed bottom-20 left-4 z-[100] md:bottom-4">
      {open ? (
        <div
          className="w-80 animate-fade-in-up rounded-xl border border-border bg-surface-raised p-4 shadow-elevated"
          role="dialog"
          aria-label="Theme switcher"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold tracking-display-tight text-copy">Theme</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-copy-subtle transition-colors hover:bg-surface-sunken hover:text-copy"
              aria-label="Close theme switcher"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="mb-3 text-xs text-copy-muted">
            Try different presets — the entire app re-skins instantly.
          </p>

          <div className="grid grid-cols-1 gap-1.5">
            {THEME_PRESETS.map((preset) => {
              const active = themeId === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setTheme(preset.id)}
                  className={cn(
                    "group flex flex-col gap-2 rounded-lg border p-2.5 text-left transition-all",
                    active
                      ? "border-primary bg-primary-light shadow-card"
                      : "border-border bg-surface hover:-translate-y-0.5 hover:border-border-strong hover:shadow-card",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold text-copy">{preset.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-copy-subtle">
                        {preset.vibe}
                      </span>
                    </div>
                    {active && (
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  <SwatchPreview preset={preset} />
                  <p className="text-xs leading-snug text-copy-muted">{preset.description}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center gap-2 border-t border-border pt-3 text-[10px] text-copy-subtle">
            <Monitor className="h-3 w-3" />
            <span>Persists across sessions · all components re-token automatically.</span>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="group flex items-center gap-2 rounded-full border border-border bg-surface-raised px-3.5 py-2 text-xs font-medium text-copy shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
        >
          <Palette className="h-4 w-4 text-primary transition-transform group-hover:rotate-12" />
          <span>Theme</span>
          <span className="rounded-full bg-primary-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            {themeId}
          </span>
        </button>
      )}
    </div>
  );
}
